import { ActivityIndicator, DeviceEventEmitter, Image, KeyboardAvoidingView, RefreshControl, TouchableOpacity, useWindowDimensions, View, VirtualizedList } from 'react-native'
import React, { useCallback, useEffect, useState } from 'react'
import { ThemeConsumer } from '@rneui/themed'
import Popover from 'react-native-popover-view'
import { useFocusEffect } from '@react-navigation/native'
import { useAppNavigation, useAppRoute } from '../../types/@react-navigation/navigation'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { removeTaxProducts, getPosTaxProducts } from '../../redux/slices/taxSlice'
import { config } from '../../shared/environment'
import { appColors, appFonts, fontSize } from '../../themes/appTheme'
import CommonDialogBox from '../../shared/commonComponents/commonDialogBox'
import CommonHeader from '../../shared/commonComponents/commonHeader'
import { ZenButton, ZenCheckBox, ZenEmptyComponent, ZenIcon, ZenLoader, ZenText, ZenToast } from 'zen-core-elements'
import { CategoryListStyle } from '../../themes/components/category/categoryListStyle'
import { SortOption } from '../../shared/commonComponents/commonSortModal'
import { ListToolbar } from '../../shared/commonComponents/listToolbar'
import { productArray } from './taxModule'
import { CustomModal } from '../../shared/commonComponents/customModal'
import moment from 'moment'

const TaxProducts = () => {
    let limit = 10
    const dispatch = useAppDispatch()
    const route = useAppRoute('TaxProducts')
    const { taxId, isView, isAllProduct, formikValues } = route.params as any
    const listOfProducts = useAppSelector(state => state.tax.getPosTaxProductRes.rows)
    const totalCount = useAppSelector(state => state.tax.getPosTaxProductRes.count)
    const loadPage = useAppSelector(state => state.tax.getPosTaxProductRes.loading)
    const buttonLoader = useAppSelector(state => state.tax.createTax?.loading || state.tax.updateTax?.loading)
    const navigation = useAppNavigation()
    const { isLandscape } = useAppSelector(state => state?.common)
    const { width } = useWindowDimensions()

    const [offset, setOffset] = useState<number | null>(null)
    const [search, setSearch] = useState<string | null>(null)
    const [refresh, setRefresh] = useState<boolean>(false)
    const [footerLoader, setFooterLoader] = useState(false)
    const [selectedProductCard, setSelectedProductCard] = useState<Set<number>>(new Set([]))
    const [sortOrder, setSortOrder] = useState<{ field: string, order: string, id: number } | null>(null)
    const [deletePopover, setDeletePopover] = useState<boolean>(false)
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const alreadyFetched = React.useRef(false)

    const sortOptionsData: SortOption[] = [
        { name: 'Product name A-Z', id: 1, field: 'name', order: 'ASC' },
        { name: 'Product name Z-A', id: 2, field: 'name', order: 'DESC' },
        { name: 'Recently created first', id: 3, field: 'created_at', order: 'DESC' },
        { name: 'Recently created last', id: 4, field: 'created_at', order: 'ASC' },
        { name: 'Recently updated first', id: 5, field: 'modified_at', order: 'DESC' },
        { name: 'Recently updated last', id: 6, field: 'modified_at', order: 'ASC' }
    ]

    const reInitializeStates = () => {
        setSearch(null)
        setSortOrder(null)
        setSelectedProductCard(new Set([]))
    }

    useEffect(() => {
        const productCardUpdate = DeviceEventEmitter.addListener("TAX_PRODUCTS_UPDATED", () => {
            reInitializeStates()
        })
        return () => {
            productCardUpdate?.remove()
        }
    }, [])

    useFocusEffect(
        useCallback(() => {
            if (isAllProduct) {
                getSelectedTaxProducts()
            } else {
                if (taxId === 0 || !taxId) {
                    getSelectedTaxProducts()
                } else {
                    if (!alreadyFetched.current) {
                        getSelectedTaxProducts()
                    }
                }
            }
            setFooterLoader(false)
        }, [isAllProduct, taxId])
    )

    const toggleOffset = () => {
        setOffset((prev) => prev !== -1 ? -1 : 0)
    }

    useEffect(() => {
        if (offset !== null) {
            getSelectedTaxProducts()
        } else {
            setRefresh(false)
        }
    }, [offset])

    useEffect(() => {
        if (search !== null) {
            const debounceSearch = setTimeout(() => {
                toggleOffset()
            }, 1000)
            return () => clearTimeout(debounceSearch)
        }
    }, [search])

    const confirmation = (item: { id: number }) => {
        if (item?.id === 2) {
            setDeletePopover(false)
            deleteMappedProduct()
        } else {
            setDeletePopover(false)
        }
    }

    const getSelectedTaxProducts = () => {
        let payloadData = {
            offset: offset && offset !== -1 ? offset : 0,
            limit: limit,
            searchText: search ? search?.trim() : '',
            sortOrder: sortOrder ? JSON.stringify({ field: sortOrder.field, order: sortOrder.order }) : JSON.stringify({ field: "name", order: "asc" }),
            taxId: taxId || null,
            isEnabledProduct: formikValues?.isEnabledAllProducts ?? false,
            isProductMapped: !isAllProduct
        }

        dispatch(getPosTaxProducts({ storeId: config?.storeId, data: payloadData })).unwrap().catch((error) => {
            console.log('Error in getSelectedTaxProducts', error)
            ZenToast({ type: 'error', message: 'Failed to get products' })
        }).finally(() => {
            setFooterLoader(false)
            setRefresh(false)
        })
    }

    const deleteMappedProduct = () => {
        const selectedIds = selectedProductCard.size > 0 ? Array.from(selectedProductCard) : deleteId !== null ? [deleteId] : []
        const mappingId: number[] = []
        listOfProducts.forEach((item: any) => {
            if (selectedIds.includes(item.id)) {
                mappingId.push(item.productTaxMappingId)
            }
        })
        console.log(formikValues?.isEnabledAllProducts, selectedIds, mappingId, "=======================");

        const data = {
            isAddProduct: false,
            isAllProductsEnabled: formikValues?.isEnabledAllProducts ?? false,
            isTaxableProducts: [],
            mappingId: formikValues?.isEnabledAllProducts ? [] : mappingId,
            productIds: formikValues?.isEnabledAllProducts ? mappingId : [],
            taxId: taxId || 0,
        }

        dispatch(removeTaxProducts({ storeId: config?.storeId, data: data })).unwrap().then((response) => {
            if (response) {
                setDeleteId(null)
                setSelectedProductCard(new Set([]))
                ZenToast({ type: 'success', message: 'Tax products removed successfully' })
                toggleOffset()
            }
        }).catch((err) => {
            console.log('Error in removing products', err)
            ZenToast({ type: 'error', message: 'Failed to remove products' })
        })
    }

    const toggleSelect = (id: number) => {
        setSelectedProductCard(prev => {
            const next = new Set(prev)
            next.has(id) ? next.delete(id) : next.add(id)
            return next
        })
    }

    const onLongPress = (productData: productArray) => {
        if (!isLandscape && selectedProductCard.size === 0) {
            toggleSelect(productData.id)
        }
    }

    const onPress = (productData: productArray) => {
        if (!isLandscape && selectedProductCard.size > 0) {
            toggleSelect(productData.id)
        }
    }

    const renderItem = ({ item, index }: { item: productArray[]; index: number }) => {
        return (
            <ThemeConsumer>
                {({ theme }) => (
                    <View key={index} style={{ flexDirection: "row" }}>
                        {item?.map((productData: productArray, responseIndex: number) => (
                            <View key={responseIndex} style={{ flex: 1 }}>
                                <TouchableOpacity disabled={isLandscape} onLongPress={() => onLongPress(productData)} onPress={() => onPress(productData)}>
                                    <View style={{ padding: 15, marginHorizontal: 10, marginBottom: 10, borderRadius: 5, backgroundColor: appColors.light, borderWidth: !isLandscape && selectedProductCard?.has(productData?.id) ? 1.5 : 0, borderColor: appColors.lightDark }}>
                                        <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                                            <View style={{ flexDirection: "row", flex: 1, alignItems: "center", }}>
                                                {(isLandscape || !isView) && (
                                                    <View style={{ width: 30 }}>
                                                        <ZenCheckBox
                                                            checked={selectedProductCard?.has(productData?.id)}
                                                            size={22}
                                                            checkedColor={appColors.primary}
                                                            disabled={isView}
                                                            onPress={() => toggleSelect(productData.id)}
                                                        />
                                                    </View>
                                                )}
                                                <View style={{ flexDirection: "column", flex: 0.2, gap: 10 }}>
                                                    <Image source={productData?.featuredImage ? { uri: config.AWSImageUrl + productData?.featuredImage } : require("../../assets/noimage.jpg")} style={{ height: 80, width: 80 }} defaultSource={require("../../assets/noimage.jpg")} />
                                                </View>
                                                <View style={{ flexDirection: "column", flex: 0.3, gap: 10 }}>
                                                    <ZenText content={productData?.name} numberOfLines={1} style={{ fontFamily: appFonts.bold, fontSize: fontSize.mediumFontSize, color: appColors.lightDark }} />
                                                </View>
                                                <View style={{ flexDirection: "column", flex: 0.5, gap: 10 }}>
                                                    <ZenText content={moment(productData?.createdAt).format("DD/MM/YYYY")} numberOfLines={1} style={{ fontFamily: appFonts.bold, fontSize: fontSize.mediumFontSize, color: appColors.lightDark }} />
                                                </View>
                                            </View>
                                            <View style={{ flexDirection: "row", alignItems: "center", gap: 10, alignSelf: "center" }}>
                                                {(taxId > 0 && !isAllProduct && isView === false) && selectedProductCard.size <= 0 ? (
                                                    <TouchableOpacity onPress={() => { setDeleteId(productData?.id); setDeletePopover(true); }}>
                                                        <ZenIcon
                                                            name="delete-outline"
                                                            type="material-community"
                                                            color={appColors.red}
                                                            size={fontSize.iconSize}
                                                            style={CategoryListStyle?.iconStyle}
                                                        />
                                                    </TouchableOpacity>
                                                ) : null}
                                            </View>
                                        </View>
                                    </View>
                                </TouchableOpacity>
                            </View>
                        ))}
                    </View>
                )}
            </ThemeConsumer>
        )
    }

    const getItem = (item: any[], index: number) => {
        let array: any = []
        let numOfColumns = 1
        for (let i = 0; i < numOfColumns; i++) {
            let temp = item?.[index * numOfColumns + i]
            temp && array?.push(temp)
        }
        return array
    }

    const footerComponent = () => {
        return (
            footerLoader ? (
                <View>
                    <ActivityIndicator size={"small"} color={appColors.primary} />
                </View>
            ) : undefined
        )
    }

    const onEndReached = () => {
        let offsetValue = offset && offset !== -1 ? offset : 0
        if (!footerLoader && (totalCount!! > offsetValue)) {
            setFooterLoader(true)
            setOffset(offsetValue + limit)
        }
    }

    const onRefresh = () => {
        setRefresh(true)
        reInitializeStates()
        toggleOffset()
    }

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <KeyboardAvoidingView style={{ flex: 1, backgroundColor: "#f8f8f8", paddingBottom: 10 }}>
                    {isAllProduct && <CommonHeader headerName={"Add Products"} backIcon backIconOnpress={() => { navigation.goBack() }} />}
                    <View style={{ flex: 1 }}>
                        <ListToolbar
                            search={{
                                value: search || "",
                                onChange: (val) => {
                                    if (typeof val === 'function') {
                                        setSearch((prev) => val(prev ?? ''));
                                    } else {
                                        setSearch(val);
                                    }
                                },
                                onCancel: () => setSearch(""),
                                placeholder: 'Search by product name',
                            }}
                            sort={{
                                selectedId: sortOrder?.id,
                                onSelect: (item) => {
                                    setSortOrder(item as { field: string, order: string, id: number }); toggleOffset();
                                },
                                title: 'Sort by',
                                options: sortOptionsData
                            }}
                            buttons={[
                                {
                                    title: !isAllProduct ? "Add Products" : "Save",
                                    disabled: isView,
                                    onPress: () => {
                                        const ids = Array.from(selectedProductCard)
                                        !isAllProduct ?
                                            (navigation.navigate("TaxProducts", { taxId, isView, isAllProduct: true, formikValues }))
                                            :
                                            DeviceEventEmitter?.emit("TaxProductsApi", ids)
                                    }
                                },
                            ]}
                        />

                        {(loadPage && !refresh && !footerLoader) ? (
                            <ZenLoader lottie={require('../../assets/infinityLoader.json')} lottieType='Local' viewStyle={{ backgroundColor: "transparent" }} />
                        ) : (
                            <View style={{ flex: 1 }}>

                                <VirtualizedList
                                    keyExtractor={(_: any, index: number) => index?.toString()}
                                    getItemCount={(data: any) => data?.length}
                                    getItem={getItem}
                                    scrollEnabled={true}
                                    nestedScrollEnabled={true}
                                    renderItem={renderItem}
                                    data={listOfProducts || []}
                                    keyboardDismissMode="on-drag"
                                    ListHeaderComponent={listOfProducts?.length ?
                                        <View>
                                            <View style={{ flexDirection: "row", alignItems: "center", marginVertical: 10 }}>
                                                <View style={{ flex: 0.2 }} />
                                                <View style={{ flex: 0.3 }}>
                                                    <ZenText content="Product Name" color={appColors.primary} bold />
                                                </View>
                                                <View style={{ flex: 0.5 }}>
                                                    <ZenText content="Created Date" color={appColors.primary} bold />
                                                </View>
                                            </View>
                                            <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingHorizontal: 10, display: selectedProductCard?.size ? "flex" : "none", paddingBottom: 10 }}>
                                                <ZenCheckBox
                                                    title={selectedProductCard?.size === listOfProducts?.length ? "Deselect All" : "Select All"}
                                                    containerStyle={{ margin: 0, padding: 0, backgroundColor: "transparent" }}
                                                    titleStyle={{ fontSize: fontSize.mediumFontSize }}
                                                    checkedColor={appColors.primary}
                                                    disabled={isView}
                                                    size={20}
                                                    checked={selectedProductCard?.size === listOfProducts?.length}
                                                    onPress={() => {
                                                        if (selectedProductCard?.size === listOfProducts?.length) {
                                                            setSelectedProductCard(new Set())
                                                        } else {
                                                            const allIds = new Set<number>()
                                                            listOfProducts?.forEach(card => allIds.add(card.id))
                                                            setSelectedProductCard(allIds)
                                                        }
                                                    }}
                                                />
                                                {(selectedProductCard.size > 0 && isLandscape && !isAllProduct) && (
                                                    <ZenButton
                                                        title="Delete"
                                                        titleStyle={{ fontSize: fontSize.errorFontSize, fontFamily: appFonts.bold }}
                                                        onPress={() => { selectedProductCard?.size > 0 ? setDeletePopover(true) : ZenToast({ type: "info", message: "Choose at least one product to remove" }) }}
                                                        buttonStyle={{ backgroundColor: appColors.red, borderRadius: 10, padding: 1 }}
                                                    />
                                                )}
                                            </View>
                                        </View> : undefined}
                                    showsVerticalScrollIndicator={false}
                                    onEndReachedThreshold={0.01}
                                    contentContainerStyle={[{ paddingBottom: listOfProducts?.length ? 10 : undefined, flexGrow: listOfProducts?.length ? undefined : 1, margin: 10 }]}
                                    ListFooterComponent={footerComponent}
                                    ListFooterComponentStyle={{ height: listOfProducts?.length ? 70 : 0 }}
                                    ListEmptyComponent={<ZenEmptyComponent viewStyle={{ backgroundColor: appColors.light, flexGrow: 1 }} />}
                                    onEndReached={onEndReached}

                                    refreshControl={<RefreshControl refreshing={refresh} onRefresh={onRefresh} />}
                                />
                            </View>
                        )}
                    </View>
                    {!loadPage && (!isLandscape) && !isAllProduct && (
                        <View>
                            <View style={CategoryListStyle.plusIconView}>
                                <ZenIcon
                                    name="plus"
                                    type={"material-community"}
                                    size={30}
                                    color={appColors.light}
                                    disabled={isView}
                                    onPress={() => {
                                        navigation.navigate("TaxProducts", { taxId, isView, isAllProduct: true, formikValues })
                                    }}
                                />
                            </View>
                        </View>
                    )}
                    <Popover isVisible={deletePopover} popoverStyle={{ width: width > 900 ? 500 : 450, borderRadius: 10 }}>
                        <CommonDialogBox type={selectedProductCard.size > 0 ? "BULKDELETE" : "DELETE"} replaceValue={selectedProductCard.size > 0 ? 'products' : "product"} confirmation={confirmation} />
                    </Popover>
                    <CustomModal visible={buttonLoader && isAllProduct} transparent>
                        <ZenLoader transparent viewStyle={{ flex: 1, justifyContent: "center", alignItems: "center", backgroundColor: "transparent" }} color={appColors.primary} lottieType="Local" lottie={require('../../assets/infinityLoader.json')} />
                    </CustomModal>
                </KeyboardAvoidingView>
            )}
        </ThemeConsumer>
    )
}

export default TaxProducts
